// src/components/LaunchSessionScreen.jsx
// Tela para lançar sessão manual/retroativa.
// Versão sem AppSidebar nesta tela para evitar warning de UNSAFE_componentWillMount vindo de dependência em StrictMode.
// Usa POST /psy/session/manual para lançamento livre.
// Sessões retroativas passam a gerar/reconciliar automaticamente um agendamento histórico no backend.
// Funciona vindo do detalhe do cliente via state { clienteId, nome, clientData }
// e também permite buscar/selecionar cliente quando aberta direto.

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  AlertTriangle,
  ArrowLeft,
  CalendarClock,
  CircleDollarSign,
  ClipboardCheck,
  ExternalLink,
  FilePlus2,
  Gauge,
  History,
  LoaderCircle,
  MapPin,
  Menu,
  ReceiptText,
  Save,
  Search,
  UserRound,
  Video,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/launchSession.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_ID: "psy_id",
  USER_NAME: "psy_usuario",
};

const SESSION_DRAFT_VERSION = 1;
const SESSION_DRAFT_MAX_AGE_MS = 48 * 60 * 60 * 1000;
const SESSION_DRAFT_STORAGE_PREFIX = "psyassist:launch-session:drafts";

const FORMAS_PAGAMENTO = [
  "Pix",
  "Dinheiro",
  "Cartão de crédito",
  "Cartão de débito",
  "Transferência",
  "Boleto",
  "Outro",
];


const readAuth = (key) =>
  sessionStorage.getItem(key) || localStorage.getItem(key) || "";

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};


function getDraftStorageKey() {
  const userId = readAuth(AUTH_KEYS.USER_ID) || "anonymous";
  return `${SESSION_DRAFT_STORAGE_PREFIX}:v${SESSION_DRAFT_VERSION}:${userId}`;
}

function readDraftStore() {
  try {
    const raw = sessionStorage.getItem(getDraftStorageKey());
    if (!raw) return { drafts: {} };

    const parsed = JSON.parse(raw);

    if (
      !parsed ||
      parsed.version !== SESSION_DRAFT_VERSION ||
      !parsed.drafts ||
      typeof parsed.drafts !== "object"
    ) {
      return { drafts: {} };
    }

    const now = Date.now();
    const validDrafts = Object.fromEntries(
      Object.entries(parsed.drafts).filter(([, draft]) => {
        const savedAt = Number(draft?.savedAt || 0);
        return savedAt > 0 && now - savedAt <= SESSION_DRAFT_MAX_AGE_MS;
      })
    );

    return { drafts: validDrafts };
  } catch (error) {
    console.warn("Não foi possível ler o rascunho da sessão:", error);
    return { drafts: {} };
  }
}

function writeDraftStore(drafts) {
  try {
    sessionStorage.setItem(
      getDraftStorageKey(),
      JSON.stringify({
        version: SESSION_DRAFT_VERSION,
        drafts: drafts || {},
      })
    );
  } catch (error) {
    console.warn("Não foi possível salvar o rascunho da sessão:", error);
  }
}

function getDraftId(clienteId) {
  return String(clienteId || "sem-cliente").trim() || "sem-cliente";
}

function saveSessionDraft(draft) {
  if (!draft || typeof draft !== "object") return null;

  const store = readDraftStore();
  const draftId = getDraftId(draft.clienteId);
  const savedAt = Date.now();

  store.drafts[draftId] = {
    ...draft,
    savedAt,
  };

  writeDraftStore(store.drafts);
  return savedAt;
}

function removeSessionDraft(clienteId) {
  const store = readDraftStore();
  const draftId = getDraftId(clienteId);

  delete store.drafts[draftId];

  // Se o formulário começou sem cliente e depois um cliente foi selecionado,
  // remove também o rascunho temporário sem cliente.
  if (draftId !== "sem-cliente") {
    delete store.drafts["sem-cliente"];
  }

  writeDraftStore(store.drafts);
}

function findDraftForRestore(initialClienteId) {
  const store = readDraftStore();
  const drafts = Object.values(store.drafts || {});

  if (!drafts.length) return null;

  if (initialClienteId) {
    return store.drafts[getDraftId(initialClienteId)] || null;
  }

  return drafts
    .filter(Boolean)
    .sort((a, b) => Number(b?.savedAt || 0) - Number(a?.savedAt || 0))[0] || null;
}

function formatDraftTime(timestamp) {
  if (!timestamp) return "";

  try {
    return new Intl.DateTimeFormat("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(timestamp));
  } catch {
    return "";
  }
}

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}



function normalizeMultiline(value = "") {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function normalizeText(value = "") {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function currentTimeHHMM() {
  const now = new Date();
  const hour = String(now.getHours()).padStart(2, "0");
  const minute = String(now.getMinutes()).padStart(2, "0");

  return `${hour}:${minute}`;
}

function isoToBR(dateValue) {
  const value = String(dateValue || "").trim();
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return "";

  return `${match[3]}/${match[2]}/${match[1]}`;
}



function formatMoneyBR(value) {
  const number = Number(value);

  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(Number.isFinite(number) ? number : 0);
}

function parseMoneyInput(value) {
  const raw = String(value || "").trim();

  if (!raw) return 0;

  const normalized = raw
    .replace(/[^\d,.-]/g, "")
    .replace(/\./g, "")
    .replace(",", ".");

  const number = Number(normalized);

  return Number.isFinite(number) ? number : 0;
}

function createLocalISO(dateValue, timeValue = "12:00") {
  const dateText = String(dateValue || "").trim();
  const timeText = String(timeValue || "12:00").trim() || "12:00";

  const dateMatch = dateText.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const timeMatch = timeText.match(/^(\d{2}):(\d{2})$/);

  if (!dateMatch || !timeMatch) return null;

  const year = Number(dateMatch[1]);
  const month = Number(dateMatch[2]);
  const day = Number(dateMatch[3]);
  const hour = Number(timeMatch[1]);
  const minute = Number(timeMatch[2]);

  const date = new Date(year, month - 1, day, hour, minute, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hour ||
    date.getMinutes() !== minute
  ) {
    return null;
  }

  return date.toISOString();
}

function isFutureDateTime(dateValue, timeValue) {
  const iso = createLocalISO(dateValue, timeValue);

  if (!iso) return false;

  return new Date(iso).getTime() > Date.now() + 5 * 60 * 1000;
}

function isRetroactive(dateValue) {
  if (!dateValue) return false;

  const selected = new Date(`${dateValue}T00:00:00`);
  const today = new Date(`${todayISO()}T00:00:00`);

  return selected.getTime() < today.getTime();
}

function getInitials(name = "") {
  const parts = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (!parts.length) return "C";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

function getClientId(client) {
  return client?._id || client?.id || client?.clienteId || "";
}

function getClientName(client) {
  return client?.nome || client?.name ;
}

function Field({
  label,
  required,
  hint,
  error,
  children,
  className = "",
}) {
  return (
    <div className={`ls-field ${className} ${error ? "ls-field--error" : ""}`}>
      <div className="ls-field__top">
        <label className="ls-label">
          {label}
          {required ? <span className="ls-req">*</span> : null}
        </label>

        {hint ? <span className="ls-hint">{hint}</span> : null}
      </div>

      {children}

      {error ? (
        <div className="ls-err" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default function LaunchSessionScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();

  const pageRef = useRef(null);
  const draftHydratedRef = useRef(false);
  const latestDraftRef = useRef(null);

  const state = location.state || {};
  const stateClient = state.clientData || state.cliente || null;

  const initialClienteId =
    state.clienteId ||
    state.clientId ||
    getClientId(stateClient) ||
    "";

  const initialClienteNome =
    state.nome ||
    state.nomeCliente ||
    getClientName(stateClient) ||
    "";

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [saving, setSaving] = useState(false);

  const [clienteId, setClienteId] = useState(initialClienteId);
  const [clienteBusca, setClienteBusca] = useState(initialClienteNome);
  const [selectedClient, setSelectedClient] = useState(
    stateClient || (initialClienteId ? { _id: initialClienteId, nome: initialClienteNome } : null)
  );
  const [showClientOptions, setShowClientOptions] = useState(false);

  const [dataSessao, setDataSessao] = useState(todayISO());
  const [horaSessao, setHoraSessao] = useState(currentTimeHHMM());
  const [tipoSessao, setTipoSessao] = useState("Presencial");
  const [duracao, setDuracao] = useState("50");
  const [valor, setValor] = useState("");
  const [pagamentoEfetuado, setPagamentoEfetuado] = useState(true);
  const [formaPagamento, setFormaPagamento] = useState("Pix");
  const [dataPagamento, setDataPagamento] = useState(todayISO());
  const [horaPagamento, setHoraPagamento] = useState(currentTimeHHMM());
  const [resumo, setResumo] = useState("");
  const [draftSavedAt, setDraftSavedAt] = useState(null);
  const [draftRestored, setDraftRestored] = useState(false);

  const [errors, setErrors] = useState({});

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const retroativa = useMemo(() => isRetroactive(dataSessao), [dataSessao]);
  const valorNumber = useMemo(() => parseMoneyInput(valor), [valor]);

  const filteredClients = useMemo(() => {
    const q = normalizeText(clienteBusca);

    if (!q) return clients.slice(0, 8);

    return clients
      .filter((client) =>
        normalizeText(
          [
            client?.nome,
            client?.email,
            client?.telefone,
            client?.cpf,
          ]
            .filter(Boolean)
            .join(" ")
        ).includes(q)
      )
      .slice(0, 8);
  }, [clients, clienteBusca]);

  const completion = useMemo(() => {
    const checks = [
      Boolean(clienteId),
      Boolean(dataSessao),
      Boolean(horaSessao),
      Boolean(tipoSessao),
      Number(duracao) > 0,
      valorNumber > 0,
      normalizeMultiline(resumo).length >= 10,
    ];

    if (pagamentoEfetuado) {
      checks.push(Boolean(formaPagamento));
      checks.push(Boolean(dataPagamento));
    }

    const filled = checks.filter(Boolean).length;

    return Math.round((filled / checks.length) * 100);
  }, [
    clienteId,
    dataSessao,
    horaSessao,
    tipoSessao,
    duracao,
    valorNumber,
    resumo,
    pagamentoEfetuado,
    formaPagamento,
    dataPagamento,
  ]);

  const showMessage = useCallback(
    (payload = {}) => {
      const type = ["success", "warning", "error", "info"].includes(
        payload.type
      )
        ? payload.type
        : "info";

      const defaultTitles = {
        success: "Sessão registrada",
        warning: "Atenção",
        error: "Não foi possível concluir",
        info: "Informação",
      };

      toast[type]({
        title: payload.title || defaultTitles[type],
        message: payload.text || "",
        details: Array.isArray(payload.details)
          ? payload.details
          : [],
        actionLabel: payload.actionLabel || "",
        onAction:
          typeof payload.onAction === "function"
            ? payload.onAction
            : null,
        duration:
          payload.duration !== undefined
            ? payload.duration
            : payload.actionLabel
              ? 0
              : 5200,
      });
    },
    [toast]
  );


  useEffect(() => {
    const draft = findDraftForRestore(initialClienteId);

    if (draft) {
      setClienteId(String(draft.clienteId || initialClienteId || ""));
      setClienteBusca(String(draft.clienteBusca || initialClienteNome || ""));

      if (draft.clienteId || draft.clienteBusca) {
        setSelectedClient({
          _id: String(draft.clienteId || initialClienteId || ""),
          nome: String(draft.clienteBusca || initialClienteNome || "Cliente"),
        });
      }

      setDataSessao(String(draft.dataSessao || todayISO()));
      setHoraSessao(String(draft.horaSessao || currentTimeHHMM()));
      setTipoSessao(String(draft.tipoSessao || "Presencial"));
      setDuracao(String(draft.duracao || "50"));
      setValor(String(draft.valor || ""));
      setPagamentoEfetuado(draft.pagamentoEfetuado !== false);
      setFormaPagamento(String(draft.formaPagamento || "Pix"));
      setDataPagamento(String(draft.dataPagamento || todayISO()));
      setHoraPagamento(String(draft.horaPagamento || currentTimeHHMM()));
      setResumo(String(draft.resumo || ""));
      setDraftSavedAt(Number(draft.savedAt || 0) || null);
      setDraftRestored(true);

      window.setTimeout(() => {
        showMessage({
          type: "info",
          title: "Rascunho recuperado",
          text: "Recuperamos automaticamente o lançamento que ainda não havia sido salvo.",
          duration: 4200,
        });
      }, 120);
    }

    draftHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentDraft = useMemo(
    () => ({
      clienteId,
      clienteBusca,
      dataSessao,
      horaSessao,
      tipoSessao,
      duracao,
      valor,
      pagamentoEfetuado,
      formaPagamento,
      dataPagamento,
      horaPagamento,
      resumo,
    }),
    [
      clienteId,
      clienteBusca,
      dataSessao,
      horaSessao,
      tipoSessao,
      duracao,
      valor,
      pagamentoEfetuado,
      formaPagamento,
      dataPagamento,
      horaPagamento,
      resumo,
    ]
  );

  useEffect(() => {
    latestDraftRef.current = currentDraft;

    if (!draftHydratedRef.current) return undefined;

    const timer = window.setTimeout(() => {
      const savedAt = saveSessionDraft(currentDraft);
      if (savedAt) {
        setDraftSavedAt(savedAt);
      }
    }, 700);

    return () => window.clearTimeout(timer);
  }, [currentDraft]);

  useEffect(() => {
    const saveImmediately = () => {
      if (!draftHydratedRef.current || !latestDraftRef.current) return;
      saveSessionDraft(latestDraftRef.current);
    };

    window.addEventListener("beforeunload", saveImmediately);

    return () => {
      saveImmediately();
      window.removeEventListener("beforeunload", saveImmediately);
    };
  }, []);

  const focusFirstError = () => {
    window.setTimeout(() => {
      const root = pageRef.current || document;
      const errorElement = root.querySelector(".ls-err");
      const target = errorElement?.closest(".ls-field, .ls-pay-card") || errorElement;

      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const focusable = target.querySelector?.(
        "input, textarea, select, button:not([disabled])"
      );

      if (focusable && typeof focusable.focus === "function") {
        focusable.focus({ preventScroll: true });
      }
    }, 80);
  };

 const authOrRedirect = () => {
  const token = readAuth(AUTH_KEYS.TOKEN);

  if (!token) {
    showMessage({
      type: "warning",
      text: "Sessão expirada. Faça login novamente para lançar a sessão.",
      actionLabel: "Ir para login",
      onAction: () => navigate("/login", { replace: true }),
    });

    return null;
  }

  return { token };
};

  const fetchClients = async () => {
    const auth = authOrRedirect();
    if (!auth) return;

    setLoadingClients(true);

    try {
      const response = await fetch(
        `${API_BASE}/psy/client`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        showMessage({
          type: "warning",
          text: "Sessão expirada. Faça login novamente.",
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      if (response.status === 404) {
        setClients([]);
        return;
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || "Não foi possível carregar clientes.");
      }

      setClients(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("Erro ao carregar clientes:", error);
      showMessage({
        type: "warning",
        text: error?.message || "Falha de comunicação ao carregar clientes.",
        actionLabel: "Tentar novamente",
        onAction: fetchClients,
      });
    } finally {
      setLoadingClients(false);
    }
  };

  useEffect(() => {
    if (!initialClienteId) {
      fetchClients();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectClient = (client) => {
    const id = getClientId(client);
    const name = getClientName(client);

    setSelectedClient(client);
    setClienteId(id);
    setClienteBusca(name);
    setShowClientOptions(false);
    setErrors((prev) => ({ ...prev, clienteId: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!clienteId) {
      nextErrors.clienteId = "Selecione o cliente da sessão.";
    }

    if (!dataSessao) {
      nextErrors.dataSessao = "Informe a data da sessão.";
    } else if (!createLocalISO(dataSessao, horaSessao)) {
      nextErrors.dataSessao = "Data ou horário da sessão inválido.";
    } else if (isFutureDateTime(dataSessao, horaSessao)) {
      nextErrors.dataSessao = "A sessão não pode ser lançada com data futura.";
    }

    if (!tipoSessao) {
      nextErrors.tipoSessao = "Informe o tipo da sessão.";
    }

    if (Number(duracao) <= 0) {
      nextErrors.duracao = "Informe uma duração válida.";
    }

    if (valorNumber <= 0) {
      nextErrors.valor = "Informe o valor da sessão.";
    }

    if (!normalizeMultiline(resumo)) {
      nextErrors.resumo = "Informe um resumo da sessão.";
    } else if (normalizeMultiline(resumo).length < 10) {
      nextErrors.resumo = "O resumo precisa ter pelo menos 10 caracteres.";
    }

    if (pagamentoEfetuado) {
      if (!formaPagamento) {
        nextErrors.formaPagamento = "Informe a forma de pagamento.";
      }

      if (!dataPagamento) {
        nextErrors.dataPagamento = "Informe a data do pagamento.";
      } else if (!createLocalISO(dataPagamento, horaPagamento)) {
        nextErrors.dataPagamento = "Data ou horário do pagamento inválido.";
      } else if (isFutureDateTime(dataPagamento, horaPagamento)) {
        nextErrors.dataPagamento = "O pagamento não pode ter data futura.";
      }
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showMessage({
        type: "warning",
        text: "Revise os campos destacados antes de salvar a sessão.",
      });
      focusFirstError();
      return false;
    }

    return true;
  };

  const buildPayload = () => {
  const payload = {
    lancamentoLivre: true,
    clienteId,
    dataSessao: createLocalISO(dataSessao, horaSessao),
    duracao: Number(duracao) || 50,
    tipoSessao,
    valor: valorNumber,
    pagamentoEfetuado,
    resumo: normalizeMultiline(resumo),
    observacoes: normalizeMultiline(resumo),
  };

    if (pagamentoEfetuado) {
      payload.formaPagamento = formaPagamento;
      payload.dataPagamento = createLocalISO(dataPagamento, horaPagamento);
      payload.observacaoPagamento = `Pagamento informado no lançamento livre da sessão.`;
    }

    return payload;
  };

  const salvarSessao = async () => {
    if (!validate()) return;

    const auth = authOrRedirect();
    if (!auth) return;

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE}/psy/session/manual`, {
  method: "POST",
  headers: {
    Authorization: `Bearer ${auth.token}`,
    "Content-Type": "application/json",
  },
  body: JSON.stringify(buildPayload()),
});

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.status === 401 || response.status === 403) {
        showMessage({
          type: "warning",
          text: "Sessão expirada. Faça login novamente.",
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        const apiMessage =
          data?.message ||
          data?.error ||
          "Não foi possível lançar a sessão.";

        throw new Error(apiMessage);
      }

      const agendaRetroativaCriada =
        Boolean(
          data?.agendamentoRetroativoAutomatico
        );

      removeSessionDraft(clienteId);
      setDraftSavedAt(null);
      setDraftRestored(false);

      showMessage({
        type: "success",
        title: "Sessão lançada",
        text: agendaRetroativaCriada
          ? pagamentoEfetuado
            ? data?.reciboEnviado
              ? "Sessão retroativa lançada, histórico criado na Agenda, receita registrada e recibo enviado."
              : "Sessão retroativa lançada, histórico criado na Agenda e receita registrada."
            : data?.pendenciaCobrancaCriada
              ? "Sessão retroativa lançada, histórico criado na Agenda e pendência adicionada às cobranças."
              : "Sessão retroativa lançada e histórico criado na Agenda."
          : pagamentoEfetuado
            ? data?.reciboEnviado
              ? "Sessão lançada com sucesso. A receita foi criada e o recibo foi enviado."
              : "Sessão lançada com sucesso. A receita foi criada; o recibo poderá ser enviado posteriormente."
            : data?.pendenciaCobrancaCriada
              ? "Sessão lançada como pendente e adicionada à área de cobranças."
              : "Sessão lançada como não paga. A pendência será sincronizada automaticamente.",
      });

      window.setTimeout(() => {
        const clientData = selectedClient || {
          _id: clienteId,
          nome: clienteBusca || "Cliente",
        };

        navigate("/clients/details", {
          replace: true,
          state: {
            clientData,
            refreshSessions: true,
          },
        });
      }, 850);
    } catch (error) {
      console.error("Erro ao lançar sessão:", error);
      showMessage({
        type: "warning",
        title: "Falha de comunicação",
        text: error?.message || "Falha de comunicação com a API.",
      });
    } finally {
      setSaving(false);
    }
  };

  const voltar = () => {
    if (saving) return;

    navigate(-1);
  };

  return (
    <div className="ls-page" ref={pageRef}>
      <div className="ls-bg" aria-hidden="true">
        <span className="ls-bg__orb ls-bg__orb--one" />
        <span className="ls-bg__orb ls-bg__orb--two" />
        <span className="ls-bg__orb ls-bg__orb--three" />
      </div>
      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="ls-topbar">
        <button
          className="ls-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu size={19} aria-hidden="true" />
        </button>

        <div className="ls-brand">
          <img src={Logo} alt="PsyAssist" className="ls-brand__logo" />
          <div className="ls-brand__text">
            <strong>Lançar sessão</strong>
            <span>
              Sessão manual/retroativa • {nomeUsuario || "Usuário"}
            </span>
          </div>
        </div>

        <div className="ls-topbar__actions">
          <button
            className="ls-btn ls-btn--ghost ls-btn--desktop"
            type="button"
            onClick={voltar}
            disabled={saving}
          >
            <ArrowLeft size={16} aria-hidden="true" />
            Voltar
          </button>

          <button
            className="ls-btn ls-btn--primary"
            type="button"
            onClick={salvarSessao}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  aria-hidden="true"
                  className="ls-icon-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} aria-hidden="true" />
                Salvar sessão
              </>
            )}
          </button>
        </div>
      </header>

      <main className="ls-main">
        <section className="ls-hero">
          <div>
            <span className="ls-kicker">
              <History size={15} aria-hidden="true" />
              Atendimento realizado
            </span>
            <h1>Lançar sessão</h1>
            <p>
              Registre uma sessão realizada fora da agenda ou de forma
              retroativa, informando se ela já foi paga ou se ficará pendente.
            </p>
          </div>

          <aside className="ls-hero-card">
            <div className="ls-hero-card__top">
              <small>
                <Gauge size={14} aria-hidden="true" />
                Completude
              </small>
              <strong>{completion}%</strong>
            </div>

            <div className="ls-progress">
              <span style={{ width: `${completion}%` }} />
            </div>

            <div className="ls-hero-card__meta">
              <span>
                <CalendarClock size={15} aria-hidden="true" />
                {dataSessao
                  ? `${isoToBR(dataSessao)} às ${horaSessao || "--:--"}`
                  : "Data ainda não informada"}
              </span>

              <span>
                <UserRound size={15} aria-hidden="true" />
                {selectedClient?.nome ||
                  clienteBusca ||
                  "Cliente não selecionado"}
              </span>
            </div>

            <p>
              {retroativa
                ? "Sessão retroativa: o histórico da Agenda será criado ou reconciliado automaticamente, sem disparar lembretes ou sincronização com o Google."
                : "Sessão manual para atendimento realizado hoje."}
            </p>
          </aside>
        </section>

        <section className="ls-wrap">
          <div className="ls-card">
            <div className="ls-card__head">
              <div>
                <span className="ls-card__eyebrow">
                  <ClipboardCheck size={14} aria-hidden="true" />
                  Sessão
                </span>
                <h2>Dados do atendimento</h2>
                <p>
                  Não é necessário procurar um agendamento. Em sessões
                  retroativas, o sistema cria ou reaproveita automaticamente o
                  registro histórico correspondente na Agenda.
                </p>
              </div>

              <div
                className={`ls-status-chip ${
                  retroativa
                    ? "ls-status-chip--warning"
                    : ""
                }`}
              >
                {retroativa ? (
                  <History size={14} aria-hidden="true" />
                ) : (
                  <FilePlus2 size={14} aria-hidden="true" />
                )}
                {retroativa ? "Retroativa" : "Manual"}
              </div>
            </div>

            <div className="ls-grid">
              <Field
                label="Cliente"
                required
                error={errors.clienteId}
                hint={loadingClients ? "Carregando..." : initialClienteId ? "Selecionado" : "Buscar"}
                className="ls-span-12"
              >
                <div className="ls-client-picker">
                  <Search
                    className="ls-client-picker__search"
                    size={18}
                    aria-hidden="true"
                  />

                  <input
                    className="ls-input"
                    value={clienteBusca}
                    onChange={(event) => {
                      setClienteBusca(event.target.value);
                      setClienteId("");
                      setSelectedClient(null);
                      setShowClientOptions(true);
                      setErrors((prev) => ({ ...prev, clienteId: "" }));

                      if (!clients.length && !loadingClients) {
                        fetchClients();
                      }
                    }}
                    onFocus={() => {
                      setShowClientOptions(true);

                      if (!clients.length && !loadingClients && !initialClienteId) {
                        fetchClients();
                      }
                    }}
                    placeholder="Busque pelo nome, e-mail, telefone ou CPF"
                    disabled={saving || Boolean(initialClienteId)}
                  />

                  {initialClienteId ? (
                    <button
                      className="ls-mini-btn"
                      type="button"
                      onClick={() => navigate("/clients/details", {
                        state: {
                          clientData: selectedClient || {
                            _id: clienteId,
                            nome: clienteBusca,
                          },
                        },
                      })}
                    >
                      <ExternalLink size={14} aria-hidden="true" />
                      Ver cliente
                    </button>
                  ) : null}

                  {showClientOptions && !initialClienteId ? (
                    <div className="ls-client-options">
                      {loadingClients ? (
                        <div className="ls-client-options__empty">Carregando clientes...</div>
                      ) : filteredClients.length ? (
                        filteredClients.map((client) => (
                          <button
                            key={getClientId(client)}
                            type="button"
                            onMouseDown={(event) => event.preventDefault()}
                            onClick={() => selectClient(client)}
                          >
                            <span>{getInitials(getClientName(client))}</span>
                            <strong>{getClientName(client)}</strong>
                            <small>{client?.email || client?.telefone || "Sem contato"}</small>
                          </button>
                        ))
                      ) : (
                        <div className="ls-client-options__empty">
                          Nenhum cliente encontrado.
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </Field>

              <Field
                label="Data da sessão"
                required
                error={errors.dataSessao}
                hint={dataSessao ? isoToBR(dataSessao) : "Obrigatória"}
                className="ls-span-6"
              >
                <input
                  className="ls-input"
                  type="date"
                  value={dataSessao}
                  max={todayISO()}
                  onChange={(event) => {
                    setDataSessao(event.target.value);
                    setErrors((prev) => ({ ...prev, dataSessao: "" }));
                  }}
                />
              </Field>

              <Field
                label="Horário"
                required
                error={errors.dataSessao}
                hint="Atendimento"
                className="ls-span-6"
              >
                <input
                  className="ls-input"
                  type="time"
                  value={horaSessao}
                  onChange={(event) => {
                    setHoraSessao(event.target.value);
                    setErrors((prev) => ({ ...prev, dataSessao: "" }));
                  }}
                />
              </Field>

              <Field
                label="Tipo de sessão"
                required
                error={errors.tipoSessao}
                className="ls-span-6"
              >
                <div className="ls-segment">
                  <button
                    type="button"
                    className={tipoSessao === "Presencial" ? "is-active" : ""}
                    onClick={() => setTipoSessao("Presencial")}
                  >
                    <MapPin size={16} aria-hidden="true" />
                    Presencial
                  </button>

                  <button
                    type="button"
                    className={tipoSessao === "Online" ? "is-active" : ""}
                    onClick={() => setTipoSessao("Online")}
                  >
                    <Video size={16} aria-hidden="true" />
                    Online
                  </button>
                </div>
              </Field>

              <Field
                label="Duração"
                required
                error={errors.duracao}
                hint="Minutos"
                className="ls-span-6"
              >
                <input
                  className="ls-input"
                  value={duracao}
                  onChange={(event) => {
                    setDuracao(onlyDigits(event.target.value).slice(0, 3));
                    setErrors((prev) => ({ ...prev, duracao: "" }));
                  }}
                  placeholder="50"
                  inputMode="numeric"
                />
              </Field>

              <Field
                label="Valor"
                required
                error={errors.valor}
                hint={valor ? formatMoneyBR(valorNumber) : "R$"}
                className="ls-span-6"
              >
                <input
                  className="ls-input"
                  value={valor}
                  onChange={(event) => {
                    setValor(event.target.value);
                    setErrors((prev) => ({ ...prev, valor: "" }));
                  }}
                  onBlur={(event) => {
                    const parsed = parseMoneyInput(event.target.value);
                    setValor(parsed > 0 ? parsed.toFixed(2).replace(".", ",") : "");
                  }}
                  placeholder="150,00"
                  inputMode="decimal"
                />
              </Field>

              <Field
                label="Pagamento"
                required
                hint={pagamentoEfetuado ? "Gera receita/recibo" : "Gera pendência"}
                className="ls-span-6"
              >
                <div className="ls-segment">
                  <button
                    type="button"
                    className={pagamentoEfetuado ? "is-active" : ""}
                    onClick={() => setPagamentoEfetuado(true)}
                  >
                    <CircleDollarSign size={16} aria-hidden="true" />
                    Pago
                  </button>

                  <button
                    type="button"
                    className={!pagamentoEfetuado ? "is-active" : ""}
                    onClick={() => setPagamentoEfetuado(false)}
                  >
                    <ReceiptText size={16} aria-hidden="true" />
                    Não pago
                  </button>
                </div>
              </Field>

              {pagamentoEfetuado ? (
                <>
                  <Field
                    label="Forma de pagamento"
                    required
                    error={errors.formaPagamento}
                    className="ls-span-6"
                  >
                    <select
                      className="ls-input"
                      value={formaPagamento}
                      onChange={(event) => {
                        setFormaPagamento(event.target.value);
                        setErrors((prev) => ({ ...prev, formaPagamento: "" }));
                      }}
                    >
                      {FORMAS_PAGAMENTO.map((forma) => (
                        <option key={forma} value={forma}>
                          {forma}
                        </option>
                      ))}
                    </select>
                  </Field>

                  <Field
                    label="Data do pagamento"
                    required
                    error={errors.dataPagamento}
                    hint={dataPagamento ? isoToBR(dataPagamento) : "Obrigatória"}
                    className="ls-span-3"
                  >
                    <input
                      className="ls-input"
                      type="date"
                      value={dataPagamento}
                      max={todayISO()}
                      onChange={(event) => {
                        setDataPagamento(event.target.value);
                        setErrors((prev) => ({ ...prev, dataPagamento: "" }));
                      }}
                    />
                  </Field>

                  <Field
                    label="Hora do pagamento"
                    required
                    error={errors.dataPagamento}
                    hint="Opcional"
                    className="ls-span-3"
                  >
                    <input
                      className="ls-input"
                      type="time"
                      value={horaPagamento}
                      onChange={(event) => {
                        setHoraPagamento(event.target.value);
                        setErrors((prev) => ({ ...prev, dataPagamento: "" }));
                      }}
                    />
                  </Field>
                </>
              ) : (
                <div className="ls-pay-card ls-span-12">
                  <div className="ls-pay-card__icon" aria-hidden="true">
                    <AlertTriangle size={21} />
                  </div>
                  <div>
                    <strong>Sessão ficará pendente</strong>
                    <span>
                      Ao salvar, o backend cria a pendência financeira, envia o
                      lembrete imediato de não pagamento e mantém a régua de
                      cobrança Premium configurada.
                    </span>
                  </div>
                </div>
              )}

              <Field
                label="Resumo da sessão"
                required
                error={errors.resumo}
                hint={`${normalizeMultiline(resumo).length} caracteres${
                  draftSavedAt
                    ? ` • Rascunho salvo às ${formatDraftTime(draftSavedAt)}`
                    : draftRestored
                      ? " • Rascunho recuperado"
                      : ""
                }`}
                className="ls-span-12"
              >
                <textarea
                  className="ls-textarea"
                  value={resumo}
                  onChange={(event) => {
                    setResumo(event.target.value);
                    setErrors((prev) => ({ ...prev, resumo: "" }));
                  }}
                  onBlur={(event) => setResumo(normalizeMultiline(event.target.value))}
                  placeholder="Ex.: Sessão lançada retroativamente. Atendimento realizado normalmente, com alinhamento de próximos passos."
                  rows={6}
                />
              </Field>
            </div>

            <div className="ls-actions">
              <button
                className="ls-btn ls-btn--ghost"
                type="button"
                onClick={voltar}
                disabled={saving}
              >
                <ArrowLeft size={16} aria-hidden="true" />
                Voltar
              </button>

              <button
                className="ls-btn ls-btn--primary"
                type="button"
                onClick={salvarSessao}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <LoaderCircle
                      size={17}
                      aria-hidden="true"
                      className="ls-icon-spin"
                    />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save size={17} aria-hidden="true" />
                    Salvar sessão
                  </>
                )}
              </button>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
